import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faEye,
  faSearch,
  faFilter,
  faSort,
  faThumbsUp,
  faShare,
  faCalendar,
  faUser,
  faTag,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { newsAPI } from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNews, setSelectedNews] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // 筛选和搜索状态
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    author: '',
    sortBy: 'publishedAt',
    sortOrder: 'desc'
  });
  
  // 分页状态
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // 获取新闻列表
  useEffect(() => {
    fetchNews();
  }, [filters, pagination.currentPage]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: filters.search,
        category: filters.category,
        status: filters.status,
        author: filters.author,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };
      
      // 清除空值
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await newsAPI.getList(params);
      if (response.data.status === 'success') {
        setNews(response.data.data.news);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.data.pagination.totalPages,
          totalItems: response.data.data.pagination.totalItems
        }));
      } else {
        setError(response.data.message || '获取新闻列表失败');
      }
    } catch (err) {
      setError(err.message || '获取新闻列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchNews();
  };

  // 处理筛选变化
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // 处理排序
  const handleSort = (field) => {
    const newOrder = filters.sortBy === field && filters.sortOrder === 'desc' ? 'asc' : 'desc';
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: newOrder
    }));
  };

  // 处理选择
  const handleSelectNews = (newsId) => {
    setSelectedNews(prev => {
      if (prev.includes(newsId)) {
        return prev.filter(id => id !== newsId);
      } else {
        return [...prev, newsId];
      }
    });
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedNews.length === news.length) {
      setSelectedNews([]);
    } else {
      setSelectedNews(news.map(item => item._id));
    }
  };

  // 删除新闻
  const handleDelete = async (newsId) => {
    try {
      const response = await newsAPI.delete(newsId);
      if (response.data.status === 'success') {
        setNews(prev => prev.filter(item => item._id !== newsId));
        setSelectedNews(prev => prev.filter(id => id !== newsId));
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } else {
        alert(response.data.message || '删除失败');
      }
    } catch (err) {
      alert(err.message || '删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedNews.length === 0) return;
    
    if (window.confirm(`确定要删除选中的 ${selectedNews.length} 篇文章吗？`)) {
      try {
        await Promise.all(selectedNews.map(id => newsAPI.delete(id)));
        setNews(prev => prev.filter(item => !selectedNews.includes(item._id)));
        setSelectedNews([]);
      } catch (err) {
        alert('批量删除失败');
      }
    }
  };

  // 获取状态样式
  const getStatusStyle = (status) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 获取状态图标
  const getStatusIcon = (status) => {
    switch (status) {
      case 'published':
        return faCheckCircle;
      case 'draft':
        return faEdit;
      case 'pending':
        return faClock;
      default:
        return faExclamationTriangle;
    }
  };

  // 获取状态文本
  const getStatusText = (status) => {
    switch (status) {
      case 'published':
        return '已发布';
      case 'draft':
        return '草稿';
      case 'pending':
        return '待审核';
      default:
        return '未知';
    }
  };

  if (loading && news.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* 页面标题和操作 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">新闻管理</h1>
            <p className="mt-1 text-sm text-gray-500">
              共 {pagination.totalItems} 篇文章
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {selectedNews.length > 0 && (
              <button
                onClick={handleBatchDelete}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                删除选中 ({selectedNews.length})
              </button>
            )}
            
            <Link
              to="/admin/news/create"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              发布新闻
            </Link>
          </div>
        </div>

        {/* 搜索和筛选 */}
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* 搜索框 */}
              <div className="md:col-span-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faSearch} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="搜索标题、内容或作者..."
                  />
                </div>
              </div>
              
              {/* 分类筛选 */}
              <div>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">所有分类</option>
                  <option value="company">公司动态</option>
                  <option value="industry">行业资讯</option>
                  <option value="product">产品更新</option>
                  <option value="technology">技术分享</option>
                </select>
              </div>
              
              {/* 状态筛选 */}
              <div>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">所有状态</option>
                  <option value="published">已发布</option>
                  <option value="draft">草稿</option>
                  <option value="pending">待审核</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                搜索
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setFilters({
                    search: '',
                    category: '',
                    status: '',
                    author: '',
                    sortBy: 'publishedAt',
                    sortOrder: 'desc'
                  });
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                重置筛选
              </button>
            </div>
          </form>
        </div>

        {/* 新闻列表 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">加载失败</h3>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                    <button
                      onClick={fetchNews}
                      className="mt-2 text-sm text-red-800 underline hover:text-red-900"
                    >
                      重试
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* 表格头部 */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedNews.length === news.length && news.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">全选</span>
                </div>
              </div>
              
              {/* 表格内容 */}
              <div className="divide-y divide-gray-200">
                {news.length > 0 ? (
                  news.map((item) => (
                    <div key={item._id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center space-x-4">
                        {/* 选择框 */}
                        <input
                          type="checkbox"
                          checked={selectedNews.includes(item._id)}
                          onChange={() => handleSelectNews(item._id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        
                        {/* 文章信息 */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-medium text-gray-900 truncate">
                                {item.title}
                              </h3>
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {item.summary}
                              </p>
                              
                              {/* 元信息 */}
                              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                                <span className="flex items-center">
                                  <FontAwesomeIcon icon={faUser} className="mr-1" />
                                  {item.authorName}
                                </span>
                                <span className="flex items-center">
                                  <FontAwesomeIcon icon={faCalendar} className="mr-1" />
                                  {new Date(item.publishedAt || item.createdAt).toLocaleDateString()}
                                </span>
                                <span className="flex items-center">
                                  <FontAwesomeIcon icon={faTag} className="mr-1" />
                                  {item.categoryLabel}
                                </span>
                                <span className="flex items-center">
                                  <FontAwesomeIcon icon={faEye} className="mr-1" />
                                  {item.views}
                                </span>
                                <span className="flex items-center">
                                  <FontAwesomeIcon icon={faThumbsUp} className="mr-1" />
                                  {item.likes}
                                </span>
                              </div>
                            </div>
                            
                            {/* 状态和操作 */}
                            <div className="flex items-center space-x-3">
                              {/* 状态标签 */}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(item.status)}`}>
                                <FontAwesomeIcon icon={getStatusIcon(item.status)} className="mr-1" />
                                {getStatusText(item.status)}
                              </span>
                              
                              {/* 操作按钮 */}
                              <div className="flex items-center space-x-2">
                                <Link
                                  to={`/news/${item._id}`}
                                  target="_blank"
                                  className="text-gray-400 hover:text-gray-600"
                                  title="预览"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </Link>
                                <Link
                                  to={`/admin/news/edit/${item._id}`}
                                  className="text-blue-400 hover:text-blue-600"
                                  title="编辑"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Link>
                                <button
                                  onClick={() => {
                                    setDeleteTarget(item);
                                    setShowDeleteModal(true);
                                  }}
                                  className="text-red-400 hover:text-red-600"
                                  title="删除"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-12 text-center">
                    <FontAwesomeIcon icon={faNewspaper} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">暂无新闻</h3>
                    <p className="text-gray-500 mb-4">还没有发布任何新闻文章</p>
                    <Link
                      to="/admin/news/create"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      发布第一篇新闻
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* 分页 */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={pagination.currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={pagination.currentPage === pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  显示第 <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> 到{' '}
                  <span className="font-medium">
                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                  </span>{' '}
                  条，共 <span className="font-medium">{pagination.totalItems}</span> 条记录
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                    disabled={pagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  
                  {/* 页码按钮 */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: pageNum }))}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.currentPage === pageNum
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 删除确认模态框 */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">确认删除</h3>
              <p className="text-sm text-gray-500 mb-4">
                确定要删除文章「{deleteTarget.title}」吗？此操作不可撤销。
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  取消
                </button>
                <button
                  onClick={() => handleDelete(deleteTarget._id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default NewsManagement;